import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/userStore';
import { LEGAL_DOCUMENT_VERSION } from '@/legal';

/**
 * Хук для работы с акцептом юридических документов
 * 
 * Проверяет принял ли юзер текущую версию документов
 * и предоставляет функцию для сохранения акцепта в БД
 */
export function useLegalAcceptance() {
  const { profile, setProfile } = useUserStore();
  const [accepting, setAccepting] = useState(false);

  // Принял ли юзер ТЕКУЩУЮ версию документов?
  const hasAcceptedCurrentVersion =
    profile?.legal_accepted_version === LEGAL_DOCUMENT_VERSION &&
    !!profile?.legal_accepted_at;

  // Юзеру нужно показать экран принятия?
  const needsAcceptance = !!profile && !hasAcceptedCurrentVersion;

  /**
   * Сохранить факт принятия документов в БД
   */
  const acceptDocuments = useCallback(async () => {
    if (!profile) return false;

    setAccepting(true);
    try {
      const now = new Date().toISOString();
      
      // Получаем IP со СВОЕГО сервера (best-effort, не критично).
      // Раньше спрашивали у api.ipify.org (США) — незадекларированная передача адреса
      // третьему лицу в момент дачи согласия. Теперь адрес отдаёт своя функция client-ip.
      let userIp: string | null = null;
      try {
        const { data } = await supabase.functions.invoke('client-ip', {
          body: {},
        });
        if (data?.ip) userIp = data.ip;
      } catch {
        // ip не критичен — продолжаем без него
      }

      // 1. Обновляем profile
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          legal_accepted_at: now,
          legal_accepted_version: LEGAL_DOCUMENT_VERSION,
          legal_accepted_ip: userIp,
          age_confirmed: true,
        })
        .eq('id', profile.id);

      if (profileError) {
        console.error('[legal] profile update error:', profileError);
        throw profileError;
      }

      // 2. Записываем в лог истории (для юридической защиты)
      try {
        await supabase.from('legal_acceptance_log').insert({
          user_id: profile.id,
          document_version: LEGAL_DOCUMENT_VERSION,
          document_types: ['oferta', 'privacy', 'consent_pd'],
          user_ip: userIp,
          user_agent: navigator.userAgent,
          age_confirmed: true,
        });
      } catch (logErr) {
        console.warn('[legal] log insert failed (non-critical):', logErr);
      }

      // 3. Обновляем локальный профиль
      setProfile({
        ...profile,
        legal_accepted_at: now,
        legal_accepted_version: LEGAL_DOCUMENT_VERSION,
        legal_accepted_ip: userIp,
        age_confirmed: true,
      });

      return true;
    } catch (e) {
      console.error('[legal] acceptDocuments failed:', e);
      return false;
    } finally {
      setAccepting(false);
    }
  }, [profile, setProfile]);

  return {
    needsAcceptance,
    hasAcceptedCurrentVersion,
    acceptDocuments,
    accepting,
    isExistingUser: !!profile?.legal_accepted_at && !hasAcceptedCurrentVersion,
  };
}
